import { createEffect, onMount } from "solid-js";
import type { Component } from "solid-js";
import { SolidStreamingMarkdown, createSolidStreamingMarkdown } from "./solid-markdown";

export { createSolidStreamingMarkdown, SolidStreamingMarkdown, type StreamingMarkdownInstance } from "./solid-markdown";
export { SolidStreamingMarkdownChildren, type MarkdownRendererOptions } from "./solid-markdown/components/ASTRenderers";
export { HighlightedCodeBlock } from "./solid-markdown/code-highlighting/HighlightedCodeBlock";

/**
 * A streaming markdown component that renders markdown content incrementally.
 *
 * This component is designed to render once and then update only as deltas are streamed
 * from the server. It provides two usage patterns:
 *
 * **Pattern 1: Content prop (simple)**
 * - Pass the full content string via the `content` prop
 * - Component automatically handles incremental updates when content changes
 * - Suitable for scenarios where you manage the full content string externally
 *
 * **Pattern 2: Ref-based append (advanced)**
 * - Access the `append` method via component ref
 * - Call `append(delta)` to incrementally add new content
 * - More efficient for real-time streaming scenarios
 * - Gives you direct control over when and what content is added
 *
 * @example
 * ```tsx
 * // Pattern 1: Using content prop
 * const [content, setContent] = createSignal("");
 *
 * // Server sends: "Hello"
 * setContent("Hello");
 * // Server sends: " world"
 * setContent("Hello world");
 *
 * <StreamingMarkdown content={content()} />
 * ```
 *
 * @example
 * ```tsx
 * // Pattern 2: Using ref and append method
 * let markdownRef: { append: (text: string) => void } | undefined;
 *
 * // Server sends: "Hello"
 * markdownRef?.append("Hello");
 * // Server sends: " world"
 * markdownRef?.append(" world");
 *
 * <StreamingMarkdown
 *   ref={markdownRef}
 *   onAppendReady={(appendFn) => {
 *     // Store the append function for later use
 *     // This is called once when the component is ready
 *   }}
 * />
 * ```
 *
 * **Important behavioral notes:**
 * - The component renders immediately and updates incrementally
 * - Only changed portions of the markdown are re-rendered for performance
 * - Deltas must be appended in order - the component maintains parsing state
 * - Use `reset()` method (via ref) to start fresh with new content
 * - The component is optimized for streaming scenarios where content grows over time
 *
 * @param props.content - The full markdown content string (Pattern 1)
 * @param props.onAppendReady - Callback that provides the append function (Pattern 2)
 * @param props.ref - Component ref that exposes append and reset methods (Pattern 2)
 */
export const StreamingMarkdown: Component<{
  content?: string;
  onAppendReady?: (append: (text: string) => void, reset: () => void) => void;
  ref?: { append: (text: string) => void; reset: () => void };
}> = (props) => {
  const streamer = createSolidStreamingMarkdown();

  // Handle content prop updates (Pattern 1)
  createEffect((oldContent: string | undefined) => {
    const content = props.content;

    // Only process if content prop is provided
    if (content !== undefined) {
      if (oldContent !== content) {
        streamer.append(content.substring(oldContent?.length || 0));
      }
      return content;
    }

    return oldContent;
  });

  // Expose append method via ref and callback (Pattern 2)
  onMount(() => {
    const appendMethod = (text: string) => streamer.append(text);
    const resetMethod = () => streamer.reset();

    // Set ref if provided
    if (props.ref) {
      props.ref.append = appendMethod;
      props.ref.reset = resetMethod;
    }

    // Call onAppendReady callback if provided
    props.onAppendReady?.(appendMethod, resetMethod);
  });

  return (
    <SolidStreamingMarkdown
      doc={streamer.doc}
      options={{
        classes: {
          document: "**:animate-fade-in wrap-break-word whitespace-break-spaces px-4",
          heading: (node) => {
            switch (node.level) {
              case 1:
                return "font-medium text-3xl mb-3 mt-4";
              case 2:
                return "font-medium text-2xl mb-3 mt-4";
              case 3:
                return "font-medium text-xl mb-3 mt-4";
              case 4:
                return "font-medium text-lg mb-3 mt-4";
              case 5:
                return "font-medium text-base mb-3 mt-4";
              case 6:
                return "font-medium text-sm mb-3 mt-4";
              default:
                return "";
            }
          },
          // paragraph: "mb-2",
          list: (node) => {
            switch (node.listData?.type) {
              case "ordered":
                return "list-decimal pl-4";
              case "bullet":
                return "list-disc pl-4";
              default:
                return "";
            }
          },
          paragraph: "mb-4",
          // text: "animate-fade-in",
          item: "mb-2",
          code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
          blockQuote: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
          tableWrapper: "overflow-x-auto w-full max-w-full rounded-lg border border-primary-border",
          table: "",
          tableRow: "border-b border-primary-border [&:last-child]:border-b-0",
          tableCell: "px-2 py-2 border-r border-primary-border [&:last-child]:border-r-0",
          tableHead: "bg-canvas text-sm font-semibold [&_td]:whitespace-nowrap",
          tableBody: "bg-tile",
          link: "text-brand underline",
        },
      }}
    />
  );
};
